import Login from "@domain/login/login";
import MainShell from "@domain/shell/main-shell/main-shell";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import AuthShell from "@domain/shell/auth-shell/auth-shell";
import { SessionProvider } from "@factory/authentication/session.context";
import LandingPage from "@domain/landing-page/landing-page";
import Dashboard from "@domain/dashboard/dashboard";
import { UserIdentityProvider } from "@core/services/user-identity.context";
import { ProtectedRoute } from "@shared/ui/protected-route/protected-route";
import Service from "@domain/service/service";
import ListService from "@domain/service/list-service/list-service";
import NewService from "@domain/service/new-service/new-service";
import EditService from "@domain/service/edit-service/edit-service";
import Tenant from "@domain/tenant/tenant";
import ListTenant from "@domain/tenant/list-tenant/list-tenant";
import NewTenant from "@domain/tenant/new-tenant/new-tenant";
import EditTenant from "@domain/tenant/edit-tenant/edit-tenant";
import Person from "@domain/person/person";
import ListPerson from "@domain/person/list-person/list-person";
import NewPerson from "@domain/person/new-person/new-person";
import EditPerson from "@domain/person/edit-person/edit-person";
import Role from "@domain/role/role";
import ListRole from "@domain/role/list-role/list-role";
import NewRole from "@domain/role/new-role/new-role";
import EditRole from "@domain/role/edit-role/edit-role";
import Member from "@domain/member/member";
import ListMember from "@domain/member/list-member/list-member";
import NewMember from "@domain/member/new-member/new-member";
import EditMember from "@domain/member/edit-member/edit-member";
import Transaction from "@domain/transaction/transaction";
import ListTransaction from "@domain/transaction/list-transaction/list-transaction";
import NewTransaction from "@domain/transaction/new-transaction/new-transaction";
import EditTransaction from "@domain/transaction/edit-transaction/edit-transaction";
import Item from "@domain/item/item";
import ListItem from "@domain/item/list-item/list-item";
import NewItem from "@domain/item/new-item/new-item";
import EditItem from "@domain/item/edit-item/edit-item";
import Scan from "@core/ui/scan/scan";
import { TransactionStepProvider } from "@domain/transaction/new-transaction/transaction-step.context";
import { NewTransactionProvider } from "@domain/transaction/new-transaction/new-transaction.context";
import EditTransactionNewAddon from "@domain/transaction/edit-transaction/edit-transaction-new-addon/edit-transaction-new-addon";
import RouteErrorBoundary from "@domain/page-error/route-error-boundary";
import Public from "@domain/public/public";
import CustomerAuthShell from "@domain/shell/customer-auth-shell/customer-auth-shell";
import Waiver from "@domain/waiver/waiver";
import ListWaiver from "@domain/waiver/list-waiver/list-waiver";
import NewWaiver from "@domain/waiver/new-waiver/new-waiver";
import EditWaiver from "@domain/waiver/edit-waiver/edit-waiver";
import TransactionWaiver from "@domain/transaction-waiver/transaction-waiver";
import ListTransactionWaiver from "@domain/transaction-waiver/list-transaction-waiver/list-transaction-waiver";
import NewTransactionWaiver from "@domain/transaction-waiver/new-transaction-waiver/new-transaction-waiver";
import EditTransactionWaiver from "@domain/transaction-waiver/edit-transaction-waiver/edit-transaction-waiver";
import NewScanTransaction from "@domain/transaction/new-scan-transaction/new-scan-transaction";
import Subscription from "@domain/subscription/subscription";
import ListSubscription from "@domain/subscription/list-subscription/list-subscription";
import EditSubscription from "@domain/subscription/edit-subscription/edit-subscription";
import NewSubscriptionWrapper from "@domain/subscription/new-subscription-wrapper";

// ROUTE IMPORT
const tenant = {
  path: "tenant",
  element: <Tenant />,
  children: [
    {
      path: "",
      element: <ListTenant />,
    },
    {
      path: "new",
      element: <NewTenant />,
    },
    {
      path: ":tenantId",
      element: <EditTenant />,
    },
  ],
};
const account = {
  path: "account",
  element: <Person />,
  children: [
    {
      path: "",
      element: <ListPerson />,
    },
    {
      path: "new",
      element: <NewPerson />,
    },
    {
      path: ":personId",
      element: <EditPerson />,
    },
  ],
};

const role = {
  path: "role",
  element: <Role />,
  children: [
    {
      path: "",
      element: <ListRole />,
    },
    {
      path: "new",
      element: <NewRole />,
    },
    {
      path: ":roleId",
      element: <EditRole />,
    },
  ],
};

const member = {
  path: "member",
  element: <Member />,
  children: [
    {
      path: "",
      element: <ListMember />,
    },
    {
      path: "new",
      element: <NewMember />,
    },
    {
      path: ":memberId",
      element: <EditMember />,
    },
  ],
};

const booking = {
  path: "booking",
  element: <Transaction />,
  children: [
    {
      path: "",
      element: <ListTransaction />,
      children: [
        {
          path: "scan",
          element: <NewScanTransaction />,
        },
      ],
    },
    {
      path: "new",
      element: (
        <TransactionStepProvider>
          <NewTransactionProvider>
            <NewTransaction />
          </NewTransactionProvider>
        </TransactionStepProvider>
      ),
    },
    {
      path: ":transactionId",
      element: <EditTransaction />,
    },
    {
      path: ":transactionId/scan",
      element: <Scan />,
    },
    {
      path: ":transactionId/new-addon",
      element: <EditTransactionNewAddon />,
    },
  ],
};

const service = {
  path: "service",
  element: <Service />,
  children: [
    {
      path: "",
      element: <ListService />,
    },
    {
      path: "new",
      element: <NewService />,
    },
    {
      path: ":serviceId",
      element: <EditService />,
    },
  ],
};
const addon = {
  path: "addon",
  element: <Item />,
  children: [
    {
      path: "",
      element: <ListItem />,
    },
    {
      path: "new",
      element: <NewItem />,
    },
    {
      path: ":itemId",
      element: <EditItem />,
    },
  ],
};

// ROUTE DEFINITION
const router = createBrowserRouter([
  {
    path: "/",
    element: <MainShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      { path: "login", element: <Login /> },
      {
        path: "public",
        element: <Public />,
        children: [
          {
            path: ":tenantSlug",
            element: <LandingPage />,
          },
        ],
      },
      {
        path: "customer",
        element: (
          <UserIdentityProvider>
            <SessionProvider>
              <ProtectedRoute>
                <CustomerAuthShell />
              </ProtectedRoute>
            </SessionProvider>
          </UserIdentityProvider>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: "",
            element: <LandingPage />,
          },
        ],
      },
      {
        path: "business",
        element: (
          <UserIdentityProvider>
            <SessionProvider>
              <ProtectedRoute>
                <AuthShell />
              </ProtectedRoute>
            </SessionProvider>
          </UserIdentityProvider>
        ),
        children: [
          {
            path: "",
            element: <Dashboard />,
          },
          tenant,
          role,
          account,
          member,
          booking,
          service,
          addon,
          {
            path: "waiver",
            element: <Waiver />,
            children: [
              {
                path: "",
                element: <ListWaiver />,
              },
              {
                path: "new",
                element: <NewWaiver />,
              },
              {
                path: ":waiverId",
                element: <EditWaiver />,
              },
            ],
          },
          {
            path: "transaction-waiver",
            element: <TransactionWaiver />,
            children: [
              {
                path: "",
                element: <ListTransactionWaiver />,
              },
              {
                path: "new",
                element: <NewTransactionWaiver />,
              },
              {
                path: ":transactionWaiverId",
                element: <EditTransactionWaiver />,
              },
            ],
          },
          {
            path: "subscription",
            element: <Subscription />,
            children: [
              {
                path: "",
                element: <ListSubscription />,
              },
              {
                path: "new",
                element: <NewSubscriptionWrapper />,
              },
              {
                path: ":subscriptionId",
                element: <EditSubscription />,
              },
            ],
          },
          // ROUTE ADD
        ],
      },
    ],
  },
]);
export function AppRoute() {
  return <RouterProvider router={router} />;
}
